import { emailApi } from "../../api/email";
import { KunSseActivity } from "../../components/genie/kunState";
import { EmailMessage } from "../../types/email";

export const EMAIL_WORKBENCH_PATH = "/station/email";
export const EMAIL_CONVERSATION_QUERY_KEY = "conversationId";
export const EMAIL_MESSAGE_QUERY_KEY = "messageId";

const NEW_MESSAGE_TITLE_ID = /new message arrived\s*:\s*(\S+)/i;

export interface NewEmailSignal {
  mailbox: string;
  messageId: string;
  conversationId: string;
}

// The email-task service only sends `title: "New Message Arrived: <id>"` and
// `parameters.mailbox`; the explicit id fields are accepted for producers that
// already include them.
export function getNewEmailSignal(payload: unknown): NewEmailSignal {
  const envelope =
    typeof payload === "object" && payload !== null ? (payload as Record<string, unknown>) : {};
  const parameters =
    typeof envelope.parameters === "object" && envelope.parameters !== null
      ? (envelope.parameters as Record<string, unknown>)
      : {};
  const title = typeof envelope.title === "string" ? envelope.title : "";
  const idFromTitle = NEW_MESSAGE_TITLE_ID.exec(title)?.[1] ?? "";
  const asString = (value: unknown) => (typeof value === "string" ? value.trim() : "");

  return {
    mailbox: asString(parameters.mailbox),
    messageId: asString(parameters.message_id) || asString(parameters.messageId) || idFromTitle,
    conversationId: asString(parameters.conversation_id) || asString(parameters.conversationId),
  };
}

export function buildEmailWorkbenchLink(
  mailbox: string,
  conversationId?: string,
  messageId?: string
): string {
  const query = new URLSearchParams();
  if (mailbox) query.set("mailbox", mailbox);
  if (conversationId) {
    query.set(EMAIL_CONVERSATION_QUERY_KEY, conversationId);
    if (messageId) query.set(EMAIL_MESSAGE_QUERY_KEY, messageId);
  }
  const queryString = query.toString();
  return queryString ? `${EMAIL_WORKBENCH_PATH}?${queryString}` : EMAIL_WORKBENCH_PATH;
}

export function formatSenderName(sender: string | undefined): string {
  const raw = (sender ?? "").trim();
  const displayName = /^"?([^"<]+?)"?\s*<[^>]+>$/.exec(raw)?.[1]?.trim();
  return displayName || raw;
}

export function describeNewEmail(
  signal: NewEmailSignal,
  message: Pick<EmailMessage, "sender" | "subject" | "msConversationId"> | null
): Pick<KunSseActivity, "message" | "link" | "linkLabel"> {
  const conversationId = message?.msConversationId || signal.conversationId;
  const link = buildEmailWorkbenchLink(signal.mailbox, conversationId, signal.messageId);
  const sender = formatSenderName(message?.sender);
  const subject = (message?.subject ?? "").trim();

  if (!sender && !subject) {
    return {
      message: signal.mailbox ? `A new email arrived in ${signal.mailbox}` : "A new email arrived",
      link,
      linkLabel: "Open inbox",
    };
  }

  const from = sender ? ` from ${sender}` : "";
  const about = subject ? `: “${subject}”` : "";
  return { message: `New email${from}${about}`, link, linkLabel: "Open email" };
}

// The SSE signal carries no subject or sender, so look the message up. The new
// message is the newest in its mailbox, so a small page sorted by date finds it.
export async function lookupNewEmailMessage(signal: NewEmailSignal): Promise<EmailMessage | null> {
  if (!signal.mailbox || !signal.messageId) return null;
  try {
    const response = await emailApi.searchMessages({ mailbox: signal.mailbox, page: 0, size: 5 });
    const messages = response?._embedded?.emailMessages ?? [];
    return (
      messages.find(
        (item) => item.id === signal.messageId || item.messageId === signal.messageId
      ) ?? null
    );
  } catch (error) {
    console.warn("Could not resolve new email details for Captain:", error);
    return null;
  }
}
